<template>
    <div class="flex h-full w-full flex-col p-2 px-4">
        <div class="flex items-center justify-between">
            <div class="text-xl my-4">Importer transaksjoner fra Excel</div>
            <div class="flex">
                <UButton size="sm" icon="i-mdi-microsoft-excel" @click="downloadSample">
                    Last ned eksempelfil
                </UButton>
            </div>
        </div>
        <ag-grid-vue
            class="grow w-full"
            :theme="agTheme"
            :column-defs="columnDefs"
            :row-data="importRows"
            :grid-options="gridOptions"
            edit-type="fullRow"
            @dragover.prevent
            @drop.prevent="onDrop"
        />
        <div class="flex gap-2 my-2">
            <button class="btn btn-primary" :disabled="!selection.length" @click="importSelection">
                Importer
                {{ selection.length }} transaksjoner(er)
            </button>
            <button class="btn btn-error" :disabled="!selection.length" @click="deleteSelection">
                Slett valgte
                ({{ selection.length }})
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import {AgGridVue} from 'ag-grid-vue3'
import {ref, shallowRef} from 'vue'
import {ColDef, GridApi, GridOptions, SelectionChangedEvent} from 'ag-grid-community'
import {useAccounts} from '@/services/account.service'
import {useVatService} from '@/vat/vat.service'
import * as XLSX from 'xlsx'
import {agTheme} from '@/agUtils/ag-theme.service'
import {useTransactionCreator} from '@/transaction/create-transaction/TransactionCreator.service'
import {v4 as uuidv4} from 'uuid'

const {saveTransaction} = useTransactionCreator()

const {findVatOptionFromCode} = useVatService()
const {findAccountOptionFromKey} = useAccounts()

type ImportTransRow = {
    id: string
    date: string
    debit: string
    credit: string
    debitVat: number
    creditVat: number
    amount: number
    description?: string,
    importStatus: 'new' | 'success' | 'error'
}

const importRows = ref<ImportTransRow[]>([])
const selection = ref<ImportTransRow[]>([])
const gridApi = shallowRef<GridApi>()

const gridOptions: GridOptions = {
    onGridReady: ({api}: any) => {
        gridApi.value = api
    },
    getRowId: (params: { data: any }) => params.data.id,
    onRowEditingStopped: ({data}: any) => {
        const index = importRows.value.findIndex(row => row.id === data.id)
        importRows.value[index] = {...data}
    },
    onSelectionChanged: (event: SelectionChangedEvent) => {
        const selectedRows = event.api.getSelectedRows()
        selection.value = [...selectedRows]
    },
    getRowStyle: ({data}: any) => {
        if (data.importStatus === 'error')
            return {backgroundColor: '#f6cece'}
        else if (data.importStatus === 'success')
            return {backgroundColor: '#e6ffe5'}
    },
    overlayNoRowsTemplate: 'Dra Excel-filer hit for å importere bilag',
    rowSelection: {
        mode: 'multiRow',
        enableClickSelection: false,
    }
}

const columnDefs: ColDef[] = [
    {
        headerName: 'Dato',
        field: 'date',
        editable: true,
        cellDataType: 'dateString',
        width: 120
    },
    {
        headerName: 'Debit',
        field: 'debit',
        width: 260,
        editable: true
    },
    {
        headerName: 'Deb MVA',
        field: 'debitVat',
        width: 120,
        editable: true
    },
    {
        headerName: 'Credit',
        field: 'credit',
        width: 120,
        editable: true
    },
    {
        headerName: 'Cred MVA',
        field: 'creditVat',
        width: 120,
        editable: true
    },
    {
        headerName: 'Beløp',
        field: 'amount',
        editable: true,
        type: 'numericColumn',
        width: 140
    },
    {
        headerName: 'Beskrivelse',
        field: 'description',
        editable: true,
        flex: 1
    },
    {
        headerName: 'Status',
        field: 'importStatus',
        editable: false,
        width: 120
    }
]


const downloadSample = () => {
    const url = 'TransImportEksempel.xlsx'
    const link = document.createElement('a')
    link.href = url
    link.download = url
    link.click()
}

const deleteSelection = () => {
    const selectedIds = selection.value.map(row => row.id)
    importRows.value = importRows.value.filter(row => !selectedIds.includes(row.id))
}

const importSelection = () => {
    const promises: Promise<any>[] = []

    selection.value.forEach(row => {

        const index = importRows.value.findIndex(r => r.id === row.id)
        const debit = findAccountOptionFromKey(row.debit)
        if (!debit) {
            row.importStatus = 'error'
            importRows.value[index] = {...row}
            return
        }
        const credit = findAccountOptionFromKey(row.credit)
        if (!credit) {
            row.importStatus = 'error'
            importRows.value[index] = {...row}
            return
        }
        const tLines = [
            {
                entryLineIndex: 0,
                date: row.date,
                accountNo: debit.accountNo,
                subAccountUuid: debit.subAccountUuid,
                vatOptionId: findVatOptionFromCode(row.debitVat)?.id || 1,
                amount: Math.abs(row.amount),
                description: row.description || '',
                tags: [],
                subAccountLines: []
            },
            {
                entryLineIndex: 0,
                date: row.date,
                accountNo: credit?.accountNo,
                subAccountUuid: credit?.subAccountUuid,
                vatOptionId: findVatOptionFromCode(row.debitVat)?.id || 1,
                amount: Math.abs(row.amount) * -1,
                description: row.description || '',
                tags: [],
                subAccountLines: []
            }
        ]

        const promise = saveTransaction([], tLines)
            .then(() => {
                row.importStatus = 'success'
            })
            .catch((err) => {
                console.error(err)
                row.importStatus = 'error'
            })
            .finally(() => {
                importRows.value[index] = {...row}
            })

        promises.push(promise)
    })

    Promise.allSettled(promises)
        .then(() => {
            selection.value = []
            gridApi.value?.redrawRows()
            gridApi.value?.deselectAll()
        })
}

const onDrop = async (event: any) => {
    const files = event.dataTransfer.files
    for (const file of files) {
        const reader = new FileReader()
        reader.onload = () => {
            const arrayBuffer = new Uint8Array(reader.result as ArrayBuffer)
            const workbook = XLSX.read(arrayBuffer)
            const sheet = workbook.Sheets[workbook.SheetNames[0]]
            const xlRows = XLSX.utils.sheet_to_json(sheet)
            xlRows.forEach((row: any) => {
                const transImport: ImportTransRow = {
                    id: uuidv4(),
                    ...row,
                    importStatus: 'new'
                }
                importRows.value.push(transImport)
            })
            gridApi.value?.redrawRows()
            gridApi.value?.refreshCells()
            gridApi.value?.setGridOption('rowData', importRows.value)
        }
        reader.readAsArrayBuffer(file)
    }
}

</script>

<style scoped>

</style>